import React, { useCallback, useEffect, useMemo, useState } from "react";

import classNames from "classnames";
import { isUndefined } from "lodash";

import {
  type GetBeneficiaryViewQuery,
  ParticipantTypes,
  type SearchParticipantsQuery,
  useHasArtCentreRelationshipQuery,
} from "@api/graphql";
import { CustomAccordion } from "@components/accordion/CustomAccordion";
import { getBeneficiaryPercentage } from "@components/account_profile/AccountRelationships";
import ParticipantName from "@components/account_profile/ParticipantName";
import { ParticipantStatusBadge } from "@components/account_profile/ParticipantStatusBadge";
import { ViewBeneficiaryModal } from "@components/beneficiary/ViewBeneficiaryModal";
import { useIsAuthUserAllowed } from "@components/permission_control/ProtectedComponent";
import { useAuthenticationContext } from "@context/authentication";
import { Role } from "@roles";
import { formatDateStringLong, isArtist, isBeneficiary, isCollaboration } from "@util/index";

import { AddBeneficiaryButton } from "../AddBeneficiaryButton";
import DetailsRow from "../DetailsRow";

import styles from "./AssociatedArtist.module.scss";

type Relations = GetBeneficiaryViewQuery["getBeneficiaryView"]["relations"];
type Participant = SearchParticipantsQuery["searchParticipants"][0];

interface AssociatedArtistProps {
  participantId: number | undefined;
  relations: Relations;
  participant: Participant | undefined;
  refetchBeneficiary: () => void;
}

interface AdminAssociatedArtistProps {
  participantId: number | undefined;
  relation: Relations[number];
  participant: Participant;
  refetchBeneficiary: () => void;
  showAddBeneficiaryButton: boolean;
}

const AdminAssociatedArtistPanel: React.FC<AdminAssociatedArtistProps> = ({
  relation,
  refetchBeneficiary,
  participant,
  participantId,
  showAddBeneficiaryButton,
}) => {
  const findParentNode = (recipient: (typeof relation.recipients)[number]) => {
    return recipient.parent_nodes.find((node) => node === relation.beneficiary.node_id.toString());
  };
  const calculatePercentage = (recipient: (typeof relation.recipients)[number]) => {
    const parentNode = findParentNode(recipient);
    return parentNode ? getBeneficiaryPercentage(recipient.attributes.percentages, Number(parentNode)) : undefined;
  };

  const getTitle = useCallback((participantType: (ParticipantTypes | undefined)[] | undefined) => {
    if (!participantType) {
      return "Beneficiary";
    }
    if (participantType.includes(ParticipantTypes.ART_CENTRE)) {
      return "Art Centre";
    } else if (participantType.includes(ParticipantTypes.CMO)) {
      return "CMO";
    } else {
      return "Beneficiary";
    }
  }, []);
  const isBeneficiaryTypeCollaboration = isCollaboration(participant?.participant_types);

  return (
    <>
      <div className={classNames("ps-4 pb-4")}>
        <>
          <h6 className={classNames("p-3", "ms-2")}>
            Beneficiaries of{" "}
            {isBeneficiaryTypeCollaboration
              ? relation.beneficiary.attributes.companyName
              : relation.beneficiary.attributes.name}
          </h6>
          {showAddBeneficiaryButton && (
            <AddBeneficiaryButton
              relation={relation}
              participant={participant}
              refetchBeneficiary={refetchBeneficiary}
            />
          )}
        </>
        {participantId && relation.recipients && relation.recipients.length > 0 && (
          <div>
            {relation.recipients
              .filter((recipient) => !isUndefined(findParentNode(recipient)))
              .map((recipient) => (
                <div className={classNames("d-flex position-relative")} key={recipient.node_id}>
                  <div className={styles["rectangle-block"]} />
                  <DetailsRow
                    details={[
                      {
                        title: getTitle(recipient.attributes.participantType),
                        value: (
                          <ParticipantName
                            name={recipient.attributes.companyName || recipient.attributes.name}
                            participantId={recipient.node_id}
                          />
                        ),
                      },
                      {
                        title: "",
                        value: <ParticipantStatusBadge status={recipient.attributes.participantStatus} />,
                        isBadge: true,
                      },
                      {
                        title: "Share",
                        value: `${calculatePercentage(recipient)}%`, // this need to be filtered by the id of the profile page.  ie beneficiaries of the artist.
                      },
                      {
                        title: "Start date",
                        value: formatDateStringLong(recipient.attributes.start_date) || "-",
                      },
                      {
                        title: "End date",
                        value: recipient.attributes.end_date
                          ? formatDateStringLong(recipient.attributes.end_date)
                          : "-",
                      },
                    ]}
                    className="mt-4"
                  />
                </div>
              ))}
          </div>
        )}
      </div>
    </>
  );
};

function shouldShowAddBeneficiary(
  hasArtCentreRelationship: boolean,
  participant: Participant | undefined,
  relations: Relations,
): boolean {
  if (!participant) {
    return false;
  }
  const isCollaborationAccount = isCollaboration(participant.participant_types);
  if (isCollaborationAccount) {
    return true;
  }

  return (
    !hasArtCentreRelationship &&
    (participant.deceased ?? false) &&
    (isArtist(participant.participant_types) || isBeneficiary(participant.participant_types)) &&
    relations.length > 0
  );
}

const AssociatedArtist: React.FC<AssociatedArtistProps> = ({
  participantId,
  relations,
  participant,
  refetchBeneficiary,
}) => {
  const { auth } = useAuthenticationContext();
  const [isViewBeneficiaryModalOpen, setIsViewBeneficiaryModalOpen] = useState<boolean>(false);
  const [selectedRootArtistId, setSelectedRootArtistId] = useState<number | null>(null); // State for root_artist_id
  const [showBeneficiaries, setShowBeneficiaries] = useState<boolean>(true);

  const { data: { hasArtCentreRelationship } = { hasArtCentreRelationship: false } } = useHasArtCentreRelationshipQuery(
    {
      variables: { artistParticipantId: participantId ? participantId : 0 },
      skip: !participantId,
    },
  );

  const showAddBeneficiaryButton: boolean = shouldShowAddBeneficiary(hasArtCentreRelationship, participant, relations);

  // Determine if the current user is part of the VA, MS, or Data team
  const isVAAdmin = useIsAuthUserAllowed([Role.VisualArtistAdmin]);
  const isMemberServices = useIsAuthUserAllowed([Role.MemberServices]);
  const isDataUser = useIsAuthUserAllowed([Role.Data]);
  const isInternalUser = isVAAdmin || isMemberServices || isDataUser;

  const handleBeneficiaryTreeClick = (rootArtistId: number) => {
    setSelectedRootArtistId(rootArtistId);
    setIsViewBeneficiaryModalOpen(true);
  };

  // Hide beneficiaries if the deceased participant is viewing their own profile
  useEffect(() => {
    const currentParticipantId = auth?.currentUserData?.participant?.participant_id;
    const isUnauthorizedUser = participant?.participant_id === currentParticipantId;
    if (participant?.deceased && isUnauthorizedUser) {
      setShowBeneficiaries(false);
    } else {
      setShowBeneficiaries(true);
    }
  }, [participant, auth]);

  // Sort relations to show collaboration accounts first
  const sortedRelations = useMemo(() => {
    return [...relations].sort((a, b) => {
      const aIsCollaboration = isCollaboration(a.root_artist.attributes.participantType);
      const bIsCollaboration = isCollaboration(b.root_artist.attributes.participantType);

      if (aIsCollaboration && !bIsCollaboration) {
        return -1;
      }
      if (!aIsCollaboration && bIsCollaboration) {
        return 1;
      }
      return 0;
    });
  }, [relations]);

  return (
    <>
      <CustomAccordion
        accordionKey="associatedArtist"
        contrastOpenStyle={false}
        header=<h5>{"Associated Artists"}</h5>
        defaultActiveKey={"associatedArtist"}
        customHeaderStyle="px-3"
      >
        {sortedRelations.map((relation) => (
          <div key={relation.root_artist.node_id} onClick={(e) => e.stopPropagation()}>
            {relation.root_artist && participantId && (
              <DetailsRow
                details={[
                  {
                    title: "Associated Artists",
                    value: (
                      <ParticipantName
                        name={relation.root_artist.attributes.name}
                        participantId={relation.root_artist.node_id}
                        participantType={relation.root_artist.attributes.participantType as ParticipantTypes[]}
                        companyName={relation.root_artist.attributes.companyName}
                      />
                    ),
                  },
                  ...(showBeneficiaries && isInternalUser
                    ? [
                        {
                          title: "",
                          value: <ParticipantStatusBadge status={relation.root_artist.attributes.participantStatus} />,
                          isBadge: true,
                        },
                        {
                          title: "Share",
                          value: `${relation.beneficiary.attributes.overall_royalty_percentage}%`,
                        },
                        {
                          title: "",
                          value: "Beneficiary Tree",
                          onClick: () => handleBeneficiaryTreeClick(relation.root_artist.node_id),
                          isClickable: true,
                        },
                      ]
                    : []),
                ]}
                className="mt-0"
              />
            )}
            {showBeneficiaries && isInternalUser && participant && (
              <AdminAssociatedArtistPanel
                participant={participant}
                participantId={participantId}
                relation={relation}
                refetchBeneficiary={refetchBeneficiary}
                showAddBeneficiaryButton={showAddBeneficiaryButton}
              />
            )}
          </div>
        ))}
      </CustomAccordion>

      {selectedRootArtistId && (
        <ViewBeneficiaryModal
          rootArtistId={selectedRootArtistId}
          isOpen={isViewBeneficiaryModalOpen}
          onClose={() => {
            setIsViewBeneficiaryModalOpen(false);
          }}
        />
      )}
    </>
  );
};

export default AssociatedArtist;
